import type { ComponentProps } from "react";
import { useTranslation } from "@privacy/translations";
import { WhiteSpace } from "@privacy/ui";
import { useGetRegistrationById, useGetRegistrationHistory } from "../../../api/registrations";
import { TenantControllerName } from "../../../shared/ConnectedTenantsList";
import { useCurrentCustomer } from "../../../root/CurrentOrganisationProvider";
import type { ConnectedTenantsHistoryValue } from "../../../api/registrations.types";
import { connectedTenantsFromHistory } from "../../../api/registrations.types";
import { DynamicGrid } from "../../../dynamicGrid";
import { DynamicHistory } from "../../../dynamicHistory";
import { logUnhandled } from "../../../api/logError";
import { PersonIcon3, TenantIcon3 } from "../../../shared/icons";
import { Drawer } from "../../../shared/Drawer";
import { RegistrationStatusChip } from "../../../shared/RegistrationStatus";
import { RegistrationDrawerHeader } from "./RegistrationDrawerHeader";
import { useRegistrationVariant } from "../RegistrationVariantProvider";

export const RegistrationHistoryDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const registrationHistoryQuery = useGetRegistrationHistory(registrationId, variant);
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);
  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!registrationByIdQuery.data) return <Drawer.Loading />;

        return (
          <>
            <RegistrationDrawerHeader
              registration={registrationByIdQuery.data}
              activeTab="history"
              statusIndicator={"hidden"}
            />

            {!registrationHistoryQuery.data ? (
              <Drawer.Loading />
            ) : (
              <DynamicHistory.Entries
                historyEntries={registrationHistoryQuery.data.history}
                renderCustomEntry={entry => {
                  switch (entry.customType) {
                    case "registrationStatus":
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t("Registration.Status")}
                          before={<RegistrationStatusChip status={entry.oldValue} />}
                          after={<RegistrationStatusChip status={entry.newValue} />}
                        />
                      );
                    case "assistantAnswersConfirmed":
                      return (
                        <DynamicHistory.AssistantAnswersConfirmedEntry
                          createdAt={entry.createdAt}
                          createdBy={entry.createdBy}
                          count={entry.newValue}
                        />
                      );
                    case "registrationName":
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t("Registration.Name")}
                          before={<DynamicHistory.SnapshotValueText>{entry.oldValue}</DynamicHistory.SnapshotValueText>}
                          after={<DynamicHistory.SnapshotValueText>{entry.newValue}</DynamicHistory.SnapshotValueText>}
                        />
                      );
                    case "registrationResponsible":
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t("RegistrationGrid.ResponsibleHeader")}
                          before={
                            <DynamicHistory.SnapshotValueText Icon={PersonIcon3}>
                              {entry.oldValue.firstName} {entry.oldValue.lastName}
                            </DynamicHistory.SnapshotValueText>
                          }
                          after={
                            <DynamicHistory.SnapshotValueText Icon={PersonIcon3}>
                              {entry.newValue.firstName} {entry.newValue.lastName}
                            </DynamicHistory.SnapshotValueText>
                          }
                        />
                      );
                    case "registrationConnectedTenant":
                      return (
                        <ConnectedTenantsChange
                          {...entry}
                          header={t(`RegistrationDrawer.ConnectedTenant.${variant}.Label`)}
                        />
                      );

                    default:
                      return logUnhandled(entry, "custom entry");
                  }
                }}
              />
            )}
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

type ConnectedTenantsChangeProps = Omit<
  ComponentProps<typeof DynamicHistory.BeforeAndAfterEntry>,
  "before" | "after"
> & {
  oldValue: ConnectedTenantsHistoryValue;
  newValue: ConnectedTenantsHistoryValue;
};

const ConnectedTenantsChange = ({ oldValue, newValue, ...entry }: ConnectedTenantsChangeProps) => (
  <DynamicHistory.BeforeAndAfterEntry
    {...entry}
    before={<ConnectedTenantsValue value={oldValue} />}
    after={<ConnectedTenantsValue value={newValue} />}
  />
);

const ConnectedTenantsValue = ({ value }: { value: ConnectedTenantsHistoryValue }) => {
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const tenants = connectedTenantsFromHistory(value);

  if (tenants.length === 0) return <DynamicHistory.EmptyValue />;

  return (
    <DynamicHistory.MultipleValues>
      {tenants.map(tenant => (
        <DynamicHistory.SnapshotValueText key={tenant.value} Icon={TenantIcon3}>
          {tenant.name}
          <WhiteSpace />
          {currentCustomer.customerKind !== "enterprise" &&
            variant === "Controller" &&
            tenant.personalDataController && (
              <TenantControllerName>{tenant.personalDataController.name}</TenantControllerName>
            )}
        </DynamicHistory.SnapshotValueText>
      ))}
    </DynamicHistory.MultipleValues>
  );
};
